// Shared look for concession charts: card header, segmented controls and Chart.js styling.
import React from 'react';
import '../concession.css';

export const CHART_THEME = {
  font: 'Outfit',
  text: '#5b6765',
  title: '#1a1a1a',
  grid: 'rgba(16, 24, 40, 0.06)',
  gameStart: '#3d4847',
  target: 'rgba(220, 38, 38, 0.55)',
};

// Header row for a chart card. The title stays in a <p> because the PDF report reads chart titles from the first <p>.
export const ChartHeader = ({ title, subtitle, controls }) => (
  <div className="cx-chart-header">
    <div style={{ minWidth: 0 }}>
      <p className="cx-chart-title">{title}</p>
      {subtitle && <div className="cx-chart-subtitle">{subtitle}</div>}
    </div>
    {controls && <div className="cx-chart-controls">{controls}</div>}
  </div>
);

export const ChartSegmented = ({ label, options, value, onChange }) => (
  <div className="cx-chart-segment-group">
    {label && <span className="cx-chart-segment-label">{label}</span>}
    <div className="cx-chart-segmented">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`cx-chart-segment ${value === option.value ? 'active' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

// Replaces the old "Bar Scale" + / - / Reset buttons. Same resolutions, picked directly.
export const ResolutionToggle = ({ resolutions, value, onChange }) => (
  <ChartSegmented
    label="Interval (min)"
    options={resolutions.map((r) => ({ value: r, label: `${r}` }))}
    value={value}
    onChange={onChange}
  />
);

// Applies shared fonts, colors, grid and tooltip styling on top of a chart's existing options.
export const applyChartTheme = (options) => {
  const font = { family: CHART_THEME.font };
  const scales = {};
  Object.entries(options.scales || {}).forEach(([key, scale]) => {
    scales[key] = {
      ...scale,
      border: { display: false, ...(scale.border || {}) },
      grid: { ...(scale.grid || {}), color: CHART_THEME.grid },
      ticks: { ...(scale.ticks || {}), color: CHART_THEME.text, font: { ...font, size: 12 } },
      title: scale.title
        ? { ...scale.title, color: CHART_THEME.text, font: { ...font, size: 12, weight: '500' } }
        : scale.title,
    };
  });

  const plugins = options.plugins || {};
  const legend = plugins.legend || {};
  const tooltip = plugins.tooltip || {};

  return {
    ...options,
    font,
    animation: options.animation ?? { duration: 350 },
    // Hover anywhere along the x axis shows every series at that time
    interaction: options.interaction || { mode: 'index', intersect: false },
    hover: options.hover || { mode: 'index', intersect: false },
    scales,
    plugins: {
      ...plugins,
      legend: {
        ...legend,
        labels: {
          ...(legend.labels || {}),
          color: CHART_THEME.title,
          font: { ...font, size: 13 },
          boxWidth: 10,
          boxHeight: 10,
          padding: 16,
        },
      },
      tooltip: {
        ...tooltip,
        backgroundColor: 'rgba(26, 26, 26, 0.92)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        titleFont: { ...font, size: 13, weight: '600' },
        bodyFont: { ...font, size: 13 },
        padding: 12,
        cornerRadius: 10,
        boxPadding: 6,
        usePointStyle: true,
      },
    },
  };
};

// ---- Line style shared with the mini charts ----

const OVERFLOW_RED = '#dc2626';

const hexToRgba = (hex, alpha) => {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// Soft vertical gradient under each line, like the mini charts
const gradientFill = (color) => (context) => {
  const { chart } = context;
  const { ctx, chartArea } = chart;
  if (!chartArea || !color.startsWith('#')) return 'transparent';
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, hexToRgba(color, 0.14));
  gradient.addColorStop(1, hexToRgba(color, 0));
  return gradient;
};

const bothOverflow = (ctx) => !!(ctx.p0?.raw?.overflow && ctx.p1?.raw?.overflow);

// Smooth line, soft fill, no points until hover; overflow stretches drawn as thick red segments
export const sectionLineDataset = (color) => ({
  borderColor: color,
  backgroundColor: gradientFill(color),
  fill: 'origin',
  borderWidth: 2,
  tension: 0.35,
  cubicInterpolationMode: 'monotone',
  // No points except overflow readings (small red dots), so single overflow readings still show
  pointRadius: (ctx) => (ctx.raw?.overflow ? 2.5 : 0),
  pointBackgroundColor: (ctx) => (ctx.raw?.overflow ? OVERFLOW_RED : color),
  pointBorderWidth: 0,
  pointHitRadius: 10,
  pointHoverRadius: 5,
  pointHoverBackgroundColor: color,
  pointHoverBorderColor: '#ffffff',
  pointHoverBorderWidth: 2,
  segment: {
    borderColor: (ctx) => (bothOverflow(ctx) ? OVERFLOW_RED : undefined),
    borderWidth: (ctx) => (bothOverflow(ctx) ? 3.5 : undefined),
  },
});

// Dashed vertical guide at the hovered time, like the mini charts
export const hoverGuidePlugin = {
  id: 'cxHoverGuide',
  afterDatasetsDraw(chart) {
    const active = chart.tooltip?.getActiveElements?.() || [];
    if (!active.length) return;
    const x = active[0].element.x;
    const { top, bottom } = chart.chartArea;
    const { ctx } = chart;
    ctx.save();
    ctx.setLineDash([3, 3]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(138, 150, 148, 0.9)';
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
    ctx.restore();
  },
};

// Faint red dashed target line (matches the mini charts)
export const targetLineAnnotation = (value, label) => ({
  type: 'line',
  scaleID: 'y',
  value,
  borderColor: 'rgba(220, 38, 38, 0.5)',
  borderWidth: 1,
  borderDash: [3, 3],
  label: {
    display: !!label,
    content: label,
    position: 'end',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: 'rgba(220, 38, 38, 0.85)',
    font: { family: CHART_THEME.font, size: 11, weight: '500' },
    padding: { x: 6, y: 2 },
    yAdjust: -10,
  },
});

// Zoom in / out / reset as compact icon buttons (for charts that zoom instead of changing interval)
export const ZoomControls = ({ chartRef, onIn, onOut, onReset }) => (
  <div className="cx-chart-segment-group">
    <span className="cx-chart-segment-label">Zoom</span>
    <div className="cx-chart-segmented">
      <button type="button" className="cx-chart-segment" title="Zoom in" aria-label="Zoom in" onClick={onIn || (() => chartRef.current?.zoom(1.25))}>
        <i className="bi bi-zoom-in" />
      </button>
      <button type="button" className="cx-chart-segment" title="Zoom out" aria-label="Zoom out" onClick={onOut || (() => chartRef.current?.zoom(0.75))}>
        <i className="bi bi-zoom-out" />
      </button>
      <button type="button" className="cx-chart-segment" title="Reset zoom" aria-label="Reset zoom" onClick={onReset || (() => chartRef.current?.resetZoom())}>
        <i className="bi bi-arrow-counterclockwise" />
      </button>
    </div>
  </div>
);
